import React, { useState } from 'react'
import { CubeIcon } from '@heroicons/react/24/outline'
import { Switch } from '../ui'
import { FormField } from '../ui/Form'
import { ProductSearchInput } from '../forms/shared'
import { useProductSearch } from '../../hooks/useProductSearch'
import type { Product } from '../../types/orderFormTypes'

interface ExportProductSkuFilterProps {
  sku: string
  onSkuChange: (sku: string) => void
}

function ExportProductSkuFilter({ sku, onSkuChange }: ExportProductSkuFilterProps) {
  const [enabled, setEnabled] = useState(false)
  const [productName, setProductName] = useState('')
  const {
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    setShowResults,
    handleSearchChange,
    clearSearch
  } = useProductSearch()

  const handleToggle = (checked: boolean) => {
    setEnabled(checked)
    if (!checked) {
      setProductName('')
      clearSearch()
      onSkuChange('')
    }
  }

  const handleSelectProduct = (product: Product) => {
    const nextSku = String(product.sku || '').trim()
    if (!nextSku) return
    setProductName(product.name || '')
    clearSearch()
    onSkuChange(nextSku)
  }

  const handleClearSku = () => {
    setProductName('')
    clearSearch()
    onSkuChange('')
  }

  return (
    <FormField
      label="Producto"
      helpText="Activa el switch para filtrar pedidos que incluyan ese producto"
      icon={CubeIcon}
      className="md:col-span-2"
    >
      <div className="flex flex-col gap-3">
        <Switch
          checked={enabled}
          onChange={handleToggle}
          label="Buscar por producto"
        />

        {enabled && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <ProductSearchInput
              searchTerm={searchTerm}
              searchResults={searchResults}
              isSearching={isSearching}
              showResults={showResults}
              onSearchChange={handleSearchChange}
              onSelectProduct={handleSelectProduct}
              onFocus={() => setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
            />

            {sku && (
              <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-800">
                <span>
                  SKU: <span className="font-semibold">{sku}</span>
                  {productName ? ` — ${productName}` : ''}
                </span>
                <button
                  type="button"
                  onClick={handleClearSku}
                  className="text-xs font-medium text-blue-700 hover:text-blue-900"
                  aria-label="Quitar producto seleccionado"
                >
                  Quitar
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </FormField>
  )
}

export default ExportProductSkuFilter
